import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { toast } from "react-toastify";
import { 
  useVehicleModelServiceV1GetVehicleTypeDropdownQuery,
  useVehicleModelServiceV1GetVehicleModelDropdownQuery,
} from "_services/modules/vehicle-model";
import { useVehicleAuxiliaryServiceV1GetVehicleAuxiliaryDropdownQuery } from "_services/modules/vehicle-auxiliary";
import { useVendorSelectOptions } from "modules/master-data/vendor/list/useVendorList";
import { 
  useFleetServiceV1GetFleetQuery,
  useFleetServiceV1UpdateFleetMutation,
  useFleetServiceV1UpdateFleetDraftMutation
} from "_services/modules/transport-resource/fleet";
import { useFleetDocumentServiceV1UploadFleetDocumentMutation } from "_services/modules/transport-resource/fleet-document";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useDriverServiceV1GetDriversQuery,
  useDriverServiceV1GetDriverDetailQuery,
  driverApi,
} from "_services/modules/transport-resource/driver";
import { fleetPageRouteName, detailFleetPageRouteName } from "../constants/routes";
import { errorHandler } from "_services/errorHandler";
import {
  validateUploadFile,
  translateFileValidationError,
  DOCUMENT_UPLOAD_OPTIONS,
  FLEET_PICTURE_OPTIONS,
} from "_helper/fileValidation";

export interface FleetDocument {
  name: string;
  expiryDate: string;
  file?: any;
  originalName?: string;
}

export interface FleetFormValues {
  fleetPhoto?: string | File;
  plateNumber: string;
  hullNumber: string;
  vehicleTypeId: string;
  vehicleModelId: string;
  associatedAuxiliaryId: string[];
  vendorId: string;
  status: string;
  odometer: number;
  lastMaintenanceDate?: string;
  documents: FleetDocument[];
  driverId: string;
}

const useEditFleet = (id: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t: tOriginal } = useAppTranslation("fleet");
  const tRef = useRef(tOriginal);
  tRef.current = tOriginal;
  const t = tOriginal;

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit") || "Edit Fleet Data",
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const schema = useMemo(() => yup.object().shape({
    fleetPhoto: yup.string().optional(),
    plateNumber: yup.string()
      .matches(/^[A-Za-z0-9 -]+$/, t("error-alphanumeric", { field: t("label-plate-number") }))
      .min(1, t("error-required", { field: t("label-plate-number") }))
      .required(t("error-required", { field: t("label-plate-number") })),
    hullNumber: yup.string()
      .matches(/^[A-Za-z0-9 -]+$/, t("error-alphanumeric", { field: t("label-hull-number") }))
      .min(1, t("error-required", { field: t("label-hull-number") }))
      .required(t("error-required", { field: t("label-hull-number") })),
    vehicleTypeId: yup.string().min(1, t("error-required", { field: t("label-vehicle-type") })).required(t("error-required", { field: t("label-vehicle-type") })),
    vehicleModelId: yup.string().min(1, t("error-required", { field: t("label-vehicle-model") })).required(t("error-required", { field: t("label-vehicle-model") })),
    associatedAuxiliaryId: yup.array().of(yup.string()).optional(),
    vendorId: yup.string().min(1, t("error-required", { field: t("label-associated-vendor") })).required(t("error-required", { field: t("label-associated-vendor") })),
    odometer: yup.number()
      .transform((value, originalValue) => (originalValue === "" || originalValue === null || isNaN(value) ? undefined : value))
      .typeError(t("error-number", { field: t("label-odometer") }))
      .moreThan(0, t("error-more-than", { field: t("label-odometer"), min: 0 }))
      .required(t("error-required", { field: t("label-odometer") })),
    lastMaintenanceDate: yup.string().optional(),
    documents: yup.array().of(
      yup.object().shape({
        name: yup.string().required(t("error-required", { field: t("label-document-name") })),
        expiryDate: yup.string().when("name", {
          is: (val: string) => ["STNK", "KIR"].includes(val),
          then: (schema) => schema.required(t("error-required", { field: t("label-expired-date") })),
          otherwise: (schema) => schema.optional(),
        }),
        file: yup.mixed().required(t("error-required", { field: t("label-upload-document") })),
      }),
    ).test("unique-document-types", t("error-duplicate-document-type", { field: t("label-document-name") }), (docs) => {
      if (!docs) return true;
      const names = docs.map((d) => d?.name).filter(Boolean);
      return new Set(names).size === names.length;
    }),
    driverId: yup.string().optional(),
  }), [t]);

  const resolver = useMemo(() => yupResolver(schema), [schema]);
  const form = useForm<FleetFormValues>({
    resolver,
    mode: "onChange",
    defaultValues: {
      fleetPhoto: "",
      plateNumber: "",
      hullNumber: "",
      vehicleTypeId: "",
      vehicleModelId: "",
      associatedAuxiliaryId: [],
      vendorId: "",
      status: "Available",
      odometer: 0,
      lastMaintenanceDate: "",
      documents: [
        { name: "STNK", expiryDate: "" },
        { name: "KIR", expiryDate: "" },
        { name: "BPKB", expiryDate: "" },
      ],
      driverId: "",
    },
  });

  const { data: detail, isLoading: isDetailLoading } = useFleetServiceV1GetFleetQuery({ id }, { skip: !id });
  const [updateFleet, { isLoading: isUpdating }] = useFleetServiceV1UpdateFleetMutation();
  const [updateFleetDraft, { isLoading: isUpdatingDraft }] = useFleetServiceV1UpdateFleetDraftMutation();
  const [uploadDocument] = useFleetDocumentServiceV1UploadFleetDocumentMutation();

  const isDraft = detail?.fleet?.status === "Draft";

  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    if (detail?.fleet && !isHydrated) {
      const fleet = detail.fleet;
      form.reset({
        fleetPhoto: fleet.photo_url || "",
        plateNumber: fleet.plate_number || "",
        hullNumber: fleet.hull_number || "",
        vehicleTypeId: fleet.vehicle_type_id || "",
        vehicleModelId: fleet.vehicle_model_id || "",
        associatedAuxiliaryId: fleet.associated_auxiliaries?.map((aux: any) => aux.id || "").filter(Boolean) || [],
        vendorId: fleet.vendor_id || "",
        status: fleet.status || "Available",
        odometer: fleet.odometer || 0,
        lastMaintenanceDate: fleet.last_maintenance_date || "",
        documents: (fleet.documents || []).map((doc: any) => ({
          name: doc.document_type || "",
          expiryDate: doc.document_type === "STNK" ? fleet.stnk_expiry : 
                      doc.document_type === "KIR" ? fleet.kir_expiry : "",
          file: doc.file_url || "",
          originalName: doc.file_url?.split("/").pop() || "",
        })),
        driverId: fleet.driver_id || "",
      });
      setIsHydrated(true);
    }
  }, [detail, form, isHydrated]);

  const selectedVehicleTypeId = form.watch("vehicleTypeId");
  const documents = form.watch("documents");

  const [expiryWarnings, setExpiryWarnings] = useState<Record<number, string | null>>({});
  const documentsStr = JSON.stringify(documents);

  useEffect(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const warnings: Record<number, string | null> = {};
    const parsedDocuments = JSON.parse(documentsStr) as FleetDocument[];

    parsedDocuments.forEach((doc, index) => {
      if ((doc.name === "STNK" || doc.name === "KIR") && doc.expiryDate) {
        const expiry = new Date(doc.expiryDate);
        if (expiry < now) {
          warnings[index] = tRef.current(doc.name === "STNK" ? "warn-stnk-expired" : "warn-kir-expired");
        } else {
          warnings[index] = null;
        }
      }
    });
    setExpiryWarnings(warnings);
  }, [documentsStr]);

  const { data: vehicleTypesData } = useVehicleModelServiceV1GetVehicleTypeDropdownQuery();
  const { data: vehicleModelsData } = useVehicleModelServiceV1GetVehicleModelDropdownQuery(
    { vehicleTypeId: selectedVehicleTypeId },
    { skip: !selectedVehicleTypeId }
  );
  const { data: auxiliaryData } = useVehicleAuxiliaryServiceV1GetVehicleAuxiliaryDropdownQuery();
  const {
    vendorOptions: rawVendorOptions,
    isLoadingVendorOptions,
    onSearchVendors,
  } = useVendorSelectOptions(10);
  const { data: driversData } = useDriverServiceV1GetDriversQuery({
    limit: 10,
    status: "Available",
  });
  const assignedDriverId = detail?.fleet?.driver_id;
  const { data: assignedDriverDetail } = useDriverServiceV1GetDriverDetailQuery(
    { id: assignedDriverId! },
    { skip: !assignedDriverId || assignedDriverId === "00000000-0000-0000-0000-000000000000" }
  );

  const [triggerGetDrivers] = driverApi.endpoints.driverServiceV1GetDrivers.useLazyQuery();

  const driverNamesRef = useRef<Record<string, string>>({});

  useEffect(() => {
    driversData?.driver?.forEach((d) => {
      if (d.id) driverNamesRef.current[d.id] = d.full_name ?? "";
    });
  }, [driversData]);

  const onSearchDrivers = useCallback(
    async (search: string) => {
      try {
        const response = await triggerGetDrivers({
          page: 1,
          limit: 10,
          status: "Available",
          search: search || undefined,
        }).unwrap();
        response.driver?.forEach((d) => {
          if (d.id) driverNamesRef.current[d.id] = d.full_name ?? "";
        });
        return (
          response.driver?.map((d) => ({
            label: d.full_name ?? "",
            value: d.id ?? "",
          })) ?? []
        );
      } catch {
        return [];
      }
    },
    [triggerGetDrivers]
  );

  const vehicleTypeOptions = useMemo(() => 
    vehicleTypesData?.vehicle_types?.map(v => ({ label: v.name ?? "", value: v.id ?? "" })) ?? [], 
    [vehicleTypesData?.vehicle_types]
  );
  const vehicleModelOptions = useMemo(() => 
    vehicleModelsData?.vehicle_models?.map(m => ({ label: m.name ?? "", value: m.id ?? "" })) ?? [], 
    [vehicleModelsData?.vehicle_models]
  );
  const freezerModelOptions = useMemo(() => 
    auxiliaryData?.vehicle_attachments?.map(a => ({ label: a.name ?? "", value: a.id ?? "" })) ?? [], 
    [auxiliaryData?.vehicle_attachments]
  );

  const selectedDriverId = form.watch("driverId");
  const selectedVendorId = form.watch("vendorId");

  const vendorOptions = useMemo(() => {
    const options = [...rawVendorOptions];
    const activeVendorId = selectedVendorId || detail?.fleet?.vendor_id;

    if (activeVendorId) {
      const exists = options.some(opt => opt.value === activeVendorId);
      if (!exists) {
        options.unshift({
          label: detail?.fleet?.vendor_name || "Assigned Vendor",
          value: activeVendorId,
        });
      }
    }
    return options;
  }, [rawVendorOptions, selectedVendorId, detail]);

  const driverOptions = useMemo(() => {
    const options = driversData?.driver?.map((d) => ({
      label: d.full_name ?? "",
      value: d.id ?? "",
    })) ?? [];

    const activeDriverId = selectedDriverId || assignedDriverId;

    if (activeDriverId && activeDriverId !== "00000000-0000-0000-0000-000000000000") {
      const exists = options.some((opt) => opt.value === activeDriverId);
      if (!exists) {
        options.unshift({
          label: driverNamesRef.current[activeDriverId] || assignedDriverDetail?.full_name || detail?.fleet?.driver_name || "Assigned Driver",
          value: activeDriverId,
        });
      }
    }
    return [{ label: tRef.current("unassigned") || "Unassigned", value: "" }, ...options];
  }, [driversData, selectedDriverId, assignedDriverId, assignedDriverDetail, detail]);

  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [isUploadingDoc, setIsUploadingDoc] = useState<Record<number, boolean>>({});
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleUploadFleetPicture = useCallback(async (file: File) => {
    const validationError = validateUploadFile(file, FLEET_PICTURE_OPTIONS);
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      form.setError("fleetPhoto" as any, { type: "manual", message });
      toast.error(message);
      return;
    }
    form.clearErrors("fleetPhoto" as any);
    form.setValue("fleetPhoto", file as any, { shouldValidate: true, shouldDirty: true });
  }, [form]);

  const handleUploadDocument = useCallback(async (index: number, file: File) => {
    const docName = form.getValues(`documents.${index}.name`);
    if (!docName) {
      toast.warn(t("toast-select-doc-type"));
      return;
    }
    const validationError = validateUploadFile(file, DOCUMENT_UPLOAD_OPTIONS);
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      form.setError(`documents.${index}.file` as any, {
        type: "manual",
        message,
      });
      toast.error(message);
      return;
    }
    form.clearErrors(`documents.${index}.file` as any);
    form.setValue(`documents.${index}.file`, file as any, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue(`documents.${index}.originalName`, file.name as any, {
      shouldDirty: true,
    } as any);
  }, [form, t]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "documents",
  });

  const handleConfirmSave = useCallback(async () => {
    const data = form.getValues();
    try {
      let fleetPictureUrl =
        typeof data.fleetPhoto === "string" ? data.fleetPhoto : "";
      if ((data as any).fleetPhoto instanceof File) {
        const formData = new FormData();
        formData.append("file", (data as any).fleetPhoto);
        const uploadRes = await uploadDocument({
          documentType: "PICTURE",
          apiHttpBody: formData as any,
        }).unwrap();
        if (uploadRes.file_url) fleetPictureUrl = uploadRes.file_url;
      }

      const uploadedDocuments = (
        await Promise.all(
          data.documents.map(async (doc) => {
            let fileUrl = typeof doc.file === "string" ? doc.file : "";
            if ((doc as any).file instanceof File) {
              const formData = new FormData();
              formData.append("file", (doc as any).file);
              const uploadRes = await uploadDocument({
                documentType: doc.name ? doc.name.toUpperCase() : "OTHER",
                apiHttpBody: formData as any,
              }).unwrap();
              if (uploadRes.file_url) fileUrl = uploadRes.file_url;
            }
            return {
              document_type: doc.name,
              file_url: fileUrl,
            };
          })
        )
      ).filter((doc) => doc.file_url !== "");

      await updateFleet({
        id,
        fleetServiceV1UpdateFleetBody: {
          plate_number: data.plateNumber,
          hull_number: data.hullNumber,
          vehicle_type_id: data.vehicleTypeId,
          vehicle_model_id: data.vehicleModelId,
          associated_auxiliary_id: data.associatedAuxiliaryId,
          vendor_id: data.vendorId,
          odometer: Number(data.odometer),
          stnk_expiry_date: data.documents.find(d => d.name === "STNK")?.expiryDate || "",
          kir_expiry_date: data.documents.find(d => d.name === "KIR")?.expiryDate || "",
          last_maintenance_date: data.lastMaintenanceDate,
          fleet_picture_url: fleetPictureUrl,
          documents: uploadedDocuments,
          status: data.status,
          driver_id: data.driverId || "",
        },
      }).unwrap();

      toast.success(t("toast-update-success", { plateNumber: data.plateNumber }));
      setTimeout(() => {
        if (location.state?.from === "detail") {
          navigate(detailFleetPageRouteName(id));
        } else {
          navigate(fleetPageRouteName);
        }
      }, 0);
    } catch (error: any) {
      errorHandler(error);
    } finally {
      setShowSaveModal(false);
    }
  }, [id, form, updateFleet, t, navigate]);

  const handleSaveDraft = useCallback(async () => {
    const data = form.getValues();
    try {
      let fleetPictureUrl =
        typeof (data as any).fleetPhoto === "string"
          ? (data as any).fleetPhoto
          : "";
      if ((data as any).fleetPhoto instanceof File) {
        const formData = new FormData();
        formData.append("file", (data as any).fleetPhoto);
        const uploadRes = await uploadDocument({
          documentType: "PICTURE",
          apiHttpBody: formData as any,
        }).unwrap();
        if (uploadRes.file_url) fleetPictureUrl = uploadRes.file_url;
      }

      const uploadedDocuments = (
        await Promise.all(
          data.documents.map(async (doc) => {
            let fileUrl = typeof doc.file === "string" ? doc.file : "";
            if ((doc as any).file instanceof File) {
              const formData = new FormData();
              formData.append("file", (doc as any).file);
              const uploadRes = await uploadDocument({
                documentType: doc.name ? doc.name.toUpperCase() : "OTHER",
                apiHttpBody: formData as any,
              }).unwrap();
              if (uploadRes.file_url) fileUrl = uploadRes.file_url;
            }
            return {
              document_type: doc.name,
              file_url: fileUrl,
            };
          })
        )
      ).filter((doc) => doc.file_url !== "");

      const odometerNum = data.odometer !== undefined && data.odometer !== null && !isNaN(Number(data.odometer))
        ? Number(data.odometer)
        : undefined;

      await updateFleetDraft({
        id,
        fleetServiceV1UpdateFleetDraftBody: {
          plate_number: data.plateNumber || undefined,
          hull_number: data.hullNumber || undefined,
          vehicle_type_id: data.vehicleTypeId || undefined,
          vehicle_model_id: data.vehicleModelId || undefined,
          associated_auxiliary_id: data.associatedAuxiliaryId && data.associatedAuxiliaryId.filter(Boolean).length > 0 ? data.associatedAuxiliaryId.filter(Boolean) : undefined,
          vendor_id: data.vendorId || undefined,
          odometer: odometerNum,
          stnk_expiry_date: data.documents.find(d => d.name === "STNK")?.expiryDate || undefined,
          kir_expiry_date: data.documents.find(d => d.name === "KIR")?.expiryDate || undefined,
          last_maintenance_date: data.lastMaintenanceDate || undefined,
          fleet_picture_url: fleetPictureUrl || undefined,
          documents: uploadedDocuments,
          driver_id: data.driverId || "",
        },
      }).unwrap();

      toast.success(t("toast-update-success", { plateNumber: data.plateNumber || t("label-draft" as any) }));
      setTimeout(() => {
        if (location.state?.from === "draft") {
          navigate("/master-data/fleet/draft");
        } else {
          navigate(fleetPageRouteName);
        }
      }, 0);
    } catch (error: any) {
      errorHandler(error);
    }
  }, [id, form, updateFleetDraft, t, navigate, location]);

  const handleSave = useCallback(() => {
    setShowSaveModal(true);
  }, []);

  return {
    t,
    form,
    handleSave,
    handleSaveDraft,
    isSubmitting: form.formState.isSubmitting || isUpdating || isUpdatingDraft,
    isDraft,
    documentFields: fields,
    handleAddDocument: useCallback(() => append({ name: "", expiryDate: "" }), [append]),
    handleRemoveDocument: useCallback((index: number) => remove(index), [remove]),
    handleConfirmSave,
    vehicleTypeOptions,
    vehicleModelOptions,
    freezerModelOptions,
    vendorOptions,
    driverOptions,
    onSearchDrivers,
    onSearchVendors,
    isUploadingPicture,
    handleUploadFleetPicture,
    isUploadingDoc,
    handleUploadDocument,
    expiryWarnings,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    isLoading: isDetailLoading,
  };
};

export default useEditFleet;
